
import React, { useState } from "react";
import { CardMedia, Box, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import noImage from "../../../../assets/noimage.jpg";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { 
  getResolvedPosterUrl, 
  getResolvedImageUrl, 
  getResolvedVideoUrl, 
  isBunnyStreamMedia,
  getBunnyEmbedUrl,
  BLACK_THUMBNAIL
} from "../../../../shared/mediaResolver";

const PostMediaViewer = ({ media = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isBunnyPlayerActive, setIsBunnyPlayerActive] = useState(false);
  const validMedia = media?.filter((item) => item?.fileUrl);

  if (validMedia.length === 0) return null;
  const currentMedia = validMedia[currentIndex] || {};

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? validMedia.length - 1 : prev - 1));
    setIsBunnyPlayerActive(false);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === validMedia.length - 1 ? 0 : prev + 1));
    setIsBunnyPlayerActive(false);
  };

  const isDoc =
    currentMedia?.mediaType?.toUpperCase() === "DOCUMENT" ||
    (currentMedia?.fileUrl && currentMedia.fileUrl.toLowerCase().endsWith(".pdf"));

  const isDirectPdf = currentMedia?.fileUrl?.toLowerCase().endsWith(".pdf") || currentMedia?.fileUrl?.startsWith("blob:");
  const encodedUrl = encodeURIComponent(currentMedia?.fileUrl || "");
  const viewerUrl = isDirectPdf
    ? `${currentMedia.fileUrl}#toolbar=0&navpanes=0&scrollbar=0`
    : `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`;

  return (
    <Box position="relative" sx={{ height: 250, width: "100%", borderRadius: "8px", overflow: "hidden", backgroundColor: "#f8fafc" }}>
      {isDoc ? (
        <>
          <iframe
            src={viewerUrl}
            title={currentMedia.fileName || "Document Preview"}
            style={{
              width: "calc(100% + 15px)",
              height: "calc(100% + 55px)",
              border: 0,
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />
          <IconButton
            component="a"
            href={currentMedia.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "absolute",
              top: 8,
              right: validMedia.length > 1 ? 44 : 8,
              backgroundColor: "rgba(255,255,255,0.9)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              "&:hover": { backgroundColor: "#ffffff" },
              zIndex: 3,
            }}
            size="small"
          >
            <OpenInNewIcon sx={{ color: "#1D4ED8", fontSize: 16 }} />
          </IconButton>
        </>
      ) : currentMedia?.mediaType === "VIDEO" ? (
        <Box position="relative" sx={{ width: "100%", height: "100%" }}>
          {isBunnyStreamMedia(currentMedia) ? (
            isBunnyPlayerActive ? (
              <iframe
                src={getBunnyEmbedUrl(getResolvedVideoUrl(currentMedia), { autoplay: true })}
                title="post video"
                style={{
                  width: "100%",
                  height: "100%",
                  border: 0,
                  display: "block",
                }}
                allow="accelerometer; gyroscope; encrypted-media; picture-in-picture;"
                allowFullScreen
              />
            ) : (
              <>
                <CardMedia
                  component="img"
                  height="250"
                  image={getResolvedPosterUrl(currentMedia, BLACK_THUMBNAIL)}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = BLACK_THUMBNAIL;
                  }}
                  sx={{ objectFit: "cover", width: "100%", height: "100%", cursor: "pointer" }}
                />
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsBunnyPlayerActive(true);
                  }}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    backgroundColor: "rgba(0,0,0,0.55)",
                    color: "white",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                    width: 50,
                    height: 50,
                    zIndex: 2,
                  }}
                >
                  <PlayArrowIcon fontSize="large" />
                </IconButton>
              </>
            )
          ) : (
            <video
              src={`${getResolvedVideoUrl(currentMedia)}#t=0.1`}
              poster={getResolvedPosterUrl(currentMedia, BLACK_THUMBNAIL)}
              controls
              style={{ objectFit: "cover", width: "100%", height: "100%", cursor: "pointer", background: "#000" }}
              preload="metadata"
            />
          )}
        </Box>
      ) : (
        <CardMedia
          component="img"
          height="250"
          image={getResolvedImageUrl(currentMedia, noImage)}
          alt="Post Media"
          sx={{ objectFit: "cover", cursor: "pointer" }}
        />
      )}

      {/* Navigation Buttons */}
      {validMedia?.length > 1 && (
        <>
          <IconButton
            onClick={handlePrev}
            sx={{
              position: "absolute",
              left: 8,
              width: "30px",
              height: "30px",
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "rgba(0,0,0,0.4)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
              zIndex: 4,
            }}
          >
            <ChevronLeft />
          </IconButton>

          <IconButton
            onClick={handleNext}
            sx={{
              position: "absolute",
              right: 8,
              width: "30px",
              height: "30px",
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "rgba(0,0,0,0.4)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
              zIndex: 4,
            }}
          >
            <ChevronRight />
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default PostMediaViewer;
